import { ANNUAL_TERMS, PLANS, isValidSubscriptionLength, type PlanTier } from '@/app/config/plans';
import type {
  AttemptStatus,
  BillingContract,
  ConfirmedPurchase,
  ExecutionTarget,
  PricingCatalog,
  PricingConfig,
  PricingQuote,
  PricingErrorBody,
  PricingVersion,
} from './types';

export const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const oneOf = (v: unknown, values: readonly unknown[]) => values.includes(v);
const cents = (v: unknown) => Number.isSafeInteger(v) && (v as number) >= 0;
const nullableCents = (v: unknown) => v === null || cents(v);
const PRICING_VERSIONS: Record<PricingVersion, true> = { web_v1: true, web_v2: true };
export const isPricingVersion = (v: unknown): v is PricingVersion =>
  typeof v === 'string' && Object.prototype.hasOwnProperty.call(PRICING_VERSIONS, v);
const version = isPricingVersion;
const nullableVersion = (v: unknown) => v === null || version(v);
const text = (v: unknown) => typeof v === 'string' && v.length > 0;
const reference = (v: unknown) => text(v) && (v as string).length <= 128 && v !== 'NA';
export const isAttemptId = (v: unknown): v is string =>
  typeof v === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const date = (v: unknown) =>
  typeof v === 'string' &&
  /^\d{4}-\d{2}-\d{2}$/.test(v) &&
  Number.isFinite(Date.parse(v)) &&
  new Date(v).toISOString().slice(0, 10) === v;
const timestamp = (v: unknown) =>
  typeof v === 'string' &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(v) &&
  Number.isFinite(Date.parse(v));
const nullableDate = (v: unknown) => v === null || date(v);
const TARGETS = [
  'subscription_create',
  'subscription_update',
  'subscription_demote',
  'payment_method_update',
] as const;
const OPERATIONS = [
  'initial',
  'manual_renewal',
  'plan_change',
  'reactivation',
  'auto_renew_restoration',
  'payment_method_update',
];
const targetOperations: Record<ExecutionTarget, string[]> = {
  subscription_create: ['initial', 'reactivation'],
  subscription_update: ['manual_renewal', 'plan_change', 'reactivation'],
  subscription_demote: ['plan_change'],
  payment_method_update: ['payment_method_update', 'auto_renew_restoration', 'reactivation'],
};
const adjustments = (v: unknown) =>
  Array.isArray(v) &&
  v.every(
    (a) =>
      isRecord(a) &&
      oneOf(a.kind, ['discount', 'add_on']) &&
      cents(a.amount_cents) &&
      Number.isSafeInteger(a.quantity) &&
      (a.quantity as number) > 0 &&
      typeof a.never_expires === 'boolean' &&
      (a.never_expires
        ? a.remaining_cycles === null
        : Number.isSafeInteger(a.remaining_cycles) && (a.remaining_cycles as number) > 0),
  );
const refunds = (v: unknown, completed = false) =>
  Array.isArray(v) &&
  v.every(
    (r) =>
      isRecord(r) &&
      reference(r.source_payment_reference) &&
      cents(r.amount_cents) &&
      (r.amount_cents as number) > 0 &&
      (!completed || reference(r.refund_reference)),
  );
const sumRefunds = (v: unknown) =>
  (v as { amount_cents: number }[]).reduce((s, r) => s + r.amount_cents, 0);

export function isPricingConfig(v: unknown): v is PricingConfig {
  if (
    !isRecord(v) ||
    v.config_schema_version !== 1 ||
    typeof v.workflow_enabled !== 'boolean' ||
    typeof v.accepted_quotes_required !== 'boolean' ||
    !version(v.pricing_catalog_version) ||
    !isRecord(v.clients)
  )
    return false;
  if (
    (!v.workflow_enabled &&
      (v.accepted_quotes_required || v.pricing_catalog_version !== 'web_v1')) ||
    (v.pricing_catalog_version === 'web_v2' && !v.accepted_quotes_required)
  )
    return false;
  for (const name of ['penzu_next', 'legacy_frontend']) {
    const client = v.clients[name];
    if (!isRecord(client) || typeof client.enabled !== 'boolean') return false;
    const expected = v.workflow_enabled
      ? client.enabled
        ? 'quoted'
        : 'paused'
      : client.enabled
        ? 'paused'
        : 'legacy';
    if (client.mode !== expected) return false;
  }
  return [
    ['quote_schema_versions', 'number'],
    ['quote_routes', 'string'],
    ['purchase_result_schema_versions', 'number'],
    ['billing_contract_schema_versions', 'number'],
  ].every(([key, kind]) => {
    const values = v[key as string];
    return (
      Array.isArray(values) &&
      new Set(values).size === values.length &&
      values.every((x) => (kind === 'number' ? Number.isSafeInteger(x) && x > 0 : text(x)))
    );
  });
}
export function supportsTarget(config: PricingConfig, target: ExecutionTarget): boolean {
  return (
    config.clients.penzu_next.mode === 'quoted' &&
    config.quote_schema_versions.includes(2) &&
    config.purchase_result_schema_versions.includes(1) &&
    config.billing_contract_schema_versions.includes(1) &&
    config.quote_routes.includes(target)
  );
}
export function isPricingCatalog(v: unknown): v is PricingCatalog {
  if (!isRecord(v) || !version(v.pricing_version) || v.currency !== 'USD' || !isRecord(v.prices))
    return false;
  return ['pro', 'pro_plus'].every((tier) => {
    const product = (v.prices as Record<string, unknown>)[tier];
    return (
      isRecord(product) &&
      isRecord(product.monthly) &&
      cents(product.monthly['1']) &&
      isRecord(product.yearly) &&
      ANNUAL_TERMS.every((term) =>
        cents((product.yearly as Record<string, unknown>)[String(term)]),
      ) &&
      (tier !== 'pro_plus' || (isRecord(product.lifetime) && cents(product.lifetime['1'])))
    );
  });
}
function financialTerms(v: Record<string, unknown>): boolean {
  if (
    v.currency !== 'USD' ||
    !oneOf(v.tier, ['pro', 'pro_plus']) ||
    !oneOf(v.interval, ['monthly', 'yearly', 'lifetime']) ||
    !oneOf(v.term, ANNUAL_TERMS) ||
    (v.interval !== 'yearly' && v.term !== 1)
  )
    return false;
  if (
    !['list_price_cents', 'discount_cents', 'credit_cents'].every((k) => cents(v[k])) ||
    !adjustments(v.renewal_adjustments)
  )
    return false;
  return v.interval === 'lifetime'
    ? v.tier === 'pro_plus' &&
        v.renewal_price_cents === null &&
        v.renewal_interval === null &&
        (v.renewal_adjustments as unknown[]).length === 0
    : cents(v.renewal_price_cents) && oneOf(v.renewal_interval, ['monthly', 'yearly']);
}
export function isPricingQuote(v: unknown): v is PricingQuote {
  if (
    !isRecord(v) ||
    v.quote_schema_version !== 2 ||
    !isAttemptId(v.quote_id) ||
    !text(v.accepted_quote) ||
    (v.accepted_quote as string).length > 8192 ||
    !timestamp(v.expires_at) ||
    !oneOf(v.execution_target, TARGETS) ||
    !oneOf(v.operation, OPERATIONS) ||
    !financialTerms(v) ||
    !nullableVersion(v.pricing_version) ||
    !oneOf(v.price_source, ['active_catalog', 'existing_contract'])
  )
    return false;
  if (
    !targetOperations[v.execution_target as ExecutionTarget].includes(v.operation as string) ||
    v.consent_required !== (v.operation !== 'payment_method_update')
  )
    return false;
  if (
    !cents(v.due_now_cents) ||
    v.due_now_cents !==
      (v.list_price_cents as number) - (v.discount_cents as number) - (v.credit_cents as number) ||
    !cents(v.refund_cents) ||
    !refunds(v.refunds) ||
    v.refund_cents !== sumRefunds(v.refunds)
  )
    return false;
  if (
    v.operation === 'payment_method_update' &&
    (v.list_price_cents !== 0 || v.due_now_cents !== 0 || v.refund_cents !== 0)
  )
    return false;
  return (
    (v.promo_code === null || typeof v.promo_code === 'string') &&
    oneOf(v.promo_attachment, [
      'none',
      'upfront',
      'subscription',
      'remaining_agreement',
      'existing_agreement',
    ]) &&
    cents(v.discount_billing_cycles) &&
    nullableDate(v.estimated_renewal_starts_on) &&
    (v.interval !== 'lifetime' || v.estimated_renewal_starts_on === null)
  );
}
export function quoteMatchesSelection(
  q: PricingQuote,
  tier: PlanTier,
  term: number,
  promo: string | null,
  target: ExecutionTarget = 'subscription_create',
): boolean {
  return (
    isValidSubscriptionLength(tier, term) &&
    q.execution_target === target &&
    q.tier === (tier.startsWith('proplus') ? 'pro_plus' : 'pro') &&
    q.interval === PLANS[tier].billingPeriod &&
    q.term === term &&
    (q.promo_code?.trim().toLowerCase() || null) === (promo?.trim().toLowerCase() || null)
  );
}
export function isConfirmedPurchase(v: unknown): v is ConfirmedPurchase {
  if (
    !isRecord(v) ||
    v.purchase_schema_version !== 1 ||
    !isAttemptId(v.purchase_id) ||
    !timestamp(v.completed_at) ||
    !financialTerms(v) ||
    v.billing_provider !== 'braintree' ||
    !nullableVersion(v.purchase_pricing_version) ||
    !nullableVersion(v.subscription_pricing_version) ||
    !oneOf(
      v.operation,
      OPERATIONS.filter((o) => o !== 'payment_method_update'),
    )
  )
    return false;
  if (v.purchase_kind !== (v.operation === 'auto_renew_restoration' ? 'reactivation' : v.operation))
    return false;
  if (
    !cents(v.charged_amount_cents) ||
    v.charged_amount_cents !==
      (v.list_price_cents as number) - (v.discount_cents as number) - (v.credit_cents as number) ||
    !cents(v.refunded_amount_cents) ||
    !refunds(v.refunds, true) ||
    v.refunded_amount_cents !== sumRefunds(v.refunds)
  )
    return false;
  return (
    (v.charged_amount_cents === 0
      ? v.payment_reference === null
      : reference(v.payment_reference)) &&
    nullableDate(v.renewal_starts_on) &&
    (v.interval !== 'lifetime' ||
      (v.subscription_pricing_version === null && v.renewal_starts_on === null))
  );
}
export function isAttemptStatus(v: unknown): v is AttemptStatus {
  return (
    isRecord(v) &&
    isAttemptId(v.attempt_id) &&
    oneOf(v.status, ['pending', 'completed', 'needs_review', 'released']) &&
    typeof v.new_attempt_allowed === 'boolean' &&
    (v.status === 'released' || !v.new_attempt_allowed)
  );
}
export function isBillingContract(v: unknown): v is BillingContract {
  if (
    !isRecord(v) ||
    v.billing_contract_schema_version !== 1 ||
    !oneOf(v.status, ['verified', 'unavailable', 'external', 'not_applicable']) ||
    !nullableVersion(v.pricing_version) ||
    !oneOf(v.billing_provider, [null, 'braintree', 'paypal', 'ios', 'android']) ||
    !oneOf(v.currency, [null, 'USD']) ||
    !oneOf(v.tier, [null, 'pro', 'pro_plus']) ||
    !oneOf(v.interval, [null, 'monthly', 'yearly', 'lifetime'])
  )
    return false;
  if (
    !['renewal_price_cents', 'next_billing_period_amount_cents'].every((k) =>
      nullableCents(v[k]),
    ) ||
    !nullableDate(v.next_billing_on) ||
    !(v.balance_cents === null || Number.isSafeInteger(v.balance_cents))
  )
    return false;
  if (v.status === 'verified')
    return (
      v.reason === null &&
      v.billing_provider === 'braintree' &&
      v.currency === 'USD' &&
      v.tier !== null &&
      oneOf(v.interval, ['monthly', 'yearly']) &&
      typeof v.auto_renew === 'boolean' &&
      oneOf(v.agreement_status, ['active', 'pending', 'past_due', 'canceled', 'expired']) &&
      cents(v.renewal_price_cents) &&
      oneOf(v.renewal_interval, ['monthly', 'yearly']) &&
      adjustments(v.renewal_adjustments) &&
      timestamp(v.verified_at)
    );
  if (
    ![
      'pricing_version',
      'agreement_status',
      'renewal_price_cents',
      'renewal_interval',
      'next_billing_period_amount_cents',
      'next_billing_on',
      'balance_cents',
      'verified_at',
    ].every((k) => v[k] === null)
  )
    return false;
  if (v.status === 'not_applicable')
    return (
      v.reason === null &&
      v.auto_renew === false &&
      Array.isArray(v.renewal_adjustments) &&
      v.renewal_adjustments.length === 0
    );
  if (v.auto_renew !== null || v.renewal_adjustments !== null) return false;
  return v.status === 'external'
    ? v.reason === null &&
        v.currency === null &&
        oneOf(v.billing_provider, ['paypal', 'ios', 'android'])
    : oneOf(v.reason, [
        'missing_contract',
        'remote_contract_not_found',
        'unresolved_contract',
        'contract_lookup_unavailable',
      ]);
}

export function isPricingError(v: unknown): v is PricingErrorBody {
  return (
    isRecord(v) &&
    typeof v.title === 'string' &&
    typeof v.message === 'string' &&
    (v.code === undefined || typeof v.code === 'string') &&
    (v.attempt_id === undefined || isAttemptId(v.attempt_id)) &&
    (v.replacement_quote === undefined || isPricingQuote(v.replacement_quote))
  );
}
